import { EXIF_FIELDS } from '../config/fieldDefinitions';
import { normalizeDateTaken } from './dateTaken';
import {
  quantizeLatitudeToNearestMeter,
  quantizeLongitudeToNearestMeter,
} from './gpsPrecision';

/**
 * Validate a field value based on its type and constraints
 * @param {string} fieldId - The field ID to validate
 * @param {any} value - The value to validate
 * @returns {{ valid: boolean, error: string | null, processedValue: any }}
 */
export function validateFieldValue(fieldId, value) {
  const field = EXIF_FIELDS[fieldId];

  if (!field) {
    return {
      valid: false,
      error: `Unknown field: ${fieldId}`,
      processedValue: null
    };
  }

  // Allow empty/null values (treated as clearing the field)
  if (value === null || value === undefined || value === '') {
    const isNullableNumberField =
      field.type === 'gps-coordinate' || field.type === 'number';
    const isDateTimeField = field.type === 'datetime-local';
    return {
      valid: true,
      error: null,
      processedValue: isNullableNumberField || isDateTimeField ? null : ''
    };
  }

  // Validate based on field type
  switch (field.type) {
    case 'gps-coordinate':
      return validateGPSCoordinate(fieldId, value);

    case 'number':
      return validateNumber(value);

    case 'datetime-local':
      return validateDateTime(value);

    case 'select':
      return validateSelect(field, value);

    case 'text':
    case 'textarea':
      return validateText(value);

    default:
      return {
        valid: true,
        error: null,
        processedValue: value
      };
  }
}

/**
 * Validate GPS coordinates
 */
function validateGPSCoordinate(fieldId, value) {
  const numValue = parseFloat(value);

  if (isNaN(numValue)) {
    return {
      valid: false,
      error: 'Invalid coordinate format. Must be a number.',
      processedValue: null
    };
  }

  // Validate latitude range
  if (fieldId === 'latitude') {
    if (numValue < -90 || numValue > 90) {
      return {
        valid: false,
        error: 'Latitude must be between -90 and 90 degrees.',
        processedValue: null
      };
    }
  }

  // Validate longitude range
  if (fieldId === 'longitude') {
    if (numValue < -180 || numValue > 180) {
      return {
        valid: false,
        error: 'Longitude must be between -180 and 180 degrees.',
        processedValue: null
      };
    }
  }

  // Validate altitude (can be negative for below sea level)
  if (fieldId === 'altitude') {
    // Reasonable range: Dead Sea (-430m) to Everest (8849m)
    // Allow broader range for safety: -500 to 10000
    if (numValue < -500 || numValue > 10000) {
      return {
        valid: false,
        error: 'Altitude must be between -500m and 10,000m.',
        processedValue: null
      };
    }
  }

  let processedValue = numValue;

  if (fieldId === 'latitude') {
    processedValue = quantizeLatitudeToNearestMeter(numValue);
  }

  if (fieldId === 'longitude') {
    processedValue = quantizeLongitudeToNearestMeter(numValue);
  }

  return {
    valid: true,
    error: null,
    processedValue
  };
}

/**
 * Validate numeric fields
 */
function validateNumber(value) {
  const numValue = parseFloat(value);

  if (isNaN(numValue)) {
    return {
      valid: false,
      error: 'Invalid number format.',
      processedValue: null
    };
  }

  // Basic sanity check for photography values
  if (numValue < 0) {
    return {
      valid: false,
      error: 'Value must be positive.',
      processedValue: null
    };
  }

  return {
    valid: true,
    error: null,
    processedValue: numValue
  };
}

/**
 * Validate date/time fields
 */
function validateDateTime(value) {
  try {
    const date = new Date(value);

    if (isNaN(date.getTime())) {
      return {
        valid: false,
        error: 'Invalid date format.',
        processedValue: null
      };
    }

    // Reasonable date range: 1900 to 2100
    const year = date.getFullYear();
    if (year < 1900 || year > 2100) {
      return {
        valid: false,
        error: 'Date must be between 1900 and 2100.',
        processedValue: null
      };
    }

    const normalized = normalizeDateTaken(value);
    if (!normalized) {
      return {
        valid: false,
        error: 'Invalid date format.',
        processedValue: null
      };
    }

    return {
      valid: true,
      error: null,
      processedValue: normalized
    };
  } catch (error) {
    return {
      valid: false,
      error: 'Invalid date format.',
      processedValue: null
    };
  }
}

/**
 * Validate select field options
 */
function validateSelect(field, value) {
  if (!field.options) {
    return {
      valid: true,
      error: null,
      processedValue: value
    };
  }

  // Handle case where value is an object {label, value} - extract the value
  let actualValue = value;
  if (typeof value === 'object' && value !== null && 'value' in value) {
    actualValue = value.value;
  }

  // Get valid values - compare as strings since select values are always strings
  const validValues = field.options.map(opt => String(opt.value));

  if (!validValues.includes(String(actualValue))) {
    return {
      valid: false,
      error: `Invalid option. Must be one of: ${validValues.join(', ')}`,
      processedValue: null
    };
  }

  return {
    valid: true,
    error: null,
    processedValue: actualValue
  };
}

/**
 * Validate text fields
 */
function validateText(value) {
  const strValue = String(value);

  // Max length for text fields (EXIF has practical limits)
  if (strValue.length > 1000) {
    return {
      valid: false,
      error: 'Text is too long (max 1000 characters).',
      processedValue: null
    };
  }

  return {
    valid: true,
    error: null,
    processedValue: strValue
  };
}

/**
 * Validate GPS coordinate pair
 * Ensures both latitude and longitude are valid when set together
 */
export function validateGPSPair(latitude, longitude) {
  const errors = [];

  if (latitude !== null && latitude !== undefined) {
    const latResult = validateGPSCoordinate('latitude', latitude);
    if (!latResult.valid) {
      errors.push(`Latitude: ${latResult.error}`);
    }
  }

  if (longitude !== null && longitude !== undefined) {
    const lonResult = validateGPSCoordinate('longitude', longitude);
    if (!lonResult.valid) {
      errors.push(`Longitude: ${lonResult.error}`);
    }
  }

  // Warn if only one coordinate is set (not an error, but potentially incomplete)
  const hasLat = latitude !== null && latitude !== undefined;
  const hasLon = longitude !== null && longitude !== undefined;

  if (hasLat && !hasLon) {
    errors.push('Warning: Latitude set without longitude.');
  } else if (!hasLat && hasLon) {
    errors.push('Warning: Longitude set without latitude.');
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings: errors.filter(e => e.startsWith('Warning'))
  };
}
